import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    visitorType: '',
    projectType: '',
    timeline: '',
    budget: '',
    message: '',
    preferredContact: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const visitorTypeOptions = [
    { value: 'recruiter', label: 'Recruiter / HR Manager' },
    { value: 'client', label: 'Potential Client' },
    { value: 'developer', label: 'Fellow Developer' },
    { value: 'startup', label: 'Startup Founder' },
    { value: 'agency', label: 'Agency Partner' },
    { value: 'other', label: 'Other' }
  ];

  const projectTypeOptions = [
    { value: 'web-app', label: 'Web Application' },
    { value: 'mobile-app', label: 'Mobile Application' },
    { value: 'ecommerce', label: 'E-commerce Platform' },
    { value: 'api', label: 'API Development' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'maintenance', label: 'Maintenance & Support' },
    { value: 'other', label: 'Other' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP (Rush Project)' },
    { value: '1-month', label: 'Within 1 Month' },
    { value: '2-3-months', label: '2-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6-months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const budgetOptions = [
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-15k', label: '$5,000 - $15,000' },
    { value: '15k-50k', label: '$15,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k+', label: '$100,000+' },
    { value: 'discuss', label: 'Let\'s Discuss' }
  ];

  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'video', label: 'Video Call' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.visitorType) newErrors.visitorType = 'Please select visitor type';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.trim().length < 20) newErrors.message = 'Message must be at least 20 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <section className="py-20 bg-surface">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Message Sent Successfully!
            </h2>
            <p className="text-text-secondary mb-8 leading-relaxed">
              Thank you for reaching out! I've received your message and will get back to you within 24 hours. 
              In the meantime, feel free to connect with me on LinkedIn or check out my latest projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                iconName="Linkedin"
                iconPosition="left"
                onClick={() => window.open('https://www.linkedin.com/in/lahiru-senavirathna-39b11a215', '_blank')}
              >
                Connect on LinkedIn
              </Button>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => setIsSubmitted(false)}
              >
                Send Another Message
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Start a Conversation
          </h2>
          <p className="text-lg text-text-secondary">
            Tell me about your project and let's explore how we can work together
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                type="text"
                name="name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                required
              />
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="john@company.com"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                required
              />
            </div>

            <Input
              label="Company / Organization"
              type="text"
              name="company"
              placeholder="Your Company Name (Optional)"
              value={formData.company}
              onChange={handleInputChange}
              description="Help me understand your background better"
            />

            {/* Project Details */}
            <div className="border-t border-border pt-8">
              <h3 className="text-lg font-semibold text-text-primary mb-6 flex items-center">
                <Icon name="Briefcase" size={20} className="mr-2" />
                Project Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Select
                  label="I am a..."
                  placeholder="Select visitor type"
                  options={visitorTypeOptions}
                  value={formData.visitorType}
                  onChange={(value) => handleSelectChange('visitorType', value)}
                  error={errors.visitorType}
                  required
                />
                <Select
                  label="Project Type"
                  placeholder="Select project type"
                  options={projectTypeOptions}
                  value={formData.projectType}
                  onChange={(value) => handleSelectChange('projectType', value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Timeline"
                  placeholder="Select timeline"
                  options={timelineOptions}
                  value={formData.timeline}
                  onChange={(value) => handleSelectChange('timeline', value)}
                />
                <Select
                  label="Budget Range"
                  placeholder="Select budget range"
                  options={budgetOptions}
                  value={formData.budget}
                  onChange={(value) => handleSelectChange('budget', value)}
                />
              </div>
            </div>

            {/* Message */}
            <div className="border-t border-border pt-8">
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Project Description *
                </label>
                <textarea
                  name="message"
                  rows={6}
                  placeholder={`Tell me about your project:\n\n• What are you trying to build?\n• What challenges are you facing?\n• What's your vision for success?\n• Any specific requirements or preferences?`}
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none ${
                    errors.message ? 'border-error' : 'border-border'
                  }`}
                  required
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-error">{errors.message}</p>
                )}
                <p className="mt-2 text-xs text-text-muted">
                  Minimum 20 characters. The more details you provide, the better I can help you.
                </p>
              </div>

              <Select
                label="Preferred Contact Method"
                placeholder="How would you like me to respond?"
                options={contactMethodOptions}
                value={formData.preferredContact}
                onChange={(value) => handleSelectChange('preferredContact', value)}
                description="I'll use this method for my initial response"
              />
            </div>

            {/* Submit Button */}
            <div className="border-t border-border pt-8">
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>
              
              <p className="text-center text-xs text-text-muted mt-4">
                By submitting this form, you agree to receive a response via your preferred contact method.
                I respect your privacy and will never share your information.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;